/**
 * Polish Wordle rules. No DOM, no Node — the web board and a later Expo client
 * have to colour tiles and reject guesses the same way.
 *
 * Copied from the legacy `wordle/app.js` product: 5 letters, 6 tries, Polish
 * alphabet, Daily (Warsaw calendar) plus Unlimited. The secret word itself stays
 * on the server; this module only scores and validates.
 */

export const WORDLE_WORD_LENGTH = 5;
export const WORDLE_MAX_GUESSES = 6;
export const WORDLE_TZ = "Europe/Warsaw";

export const WORDLE_KEY_ROWS: readonly (readonly string[])[] = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Ą", "Ć", "Ę", "Ł", "Ń", "Ó", "Ś", "Ź", "Ż"],
  ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"],
];

const WORDLE_LETTER_RE = /^[A-ZĄĆĘŁŃÓŚŹŻ]$/;
const WORDLE_WORD_RE = /^[A-ZĄĆĘŁŃÓŚŹŻ]{5}$/;

export type WordleMode = "daily" | "unlimited";
export type WordleColor = "correct" | "present" | "absent";
export type WordleGuessReject = "finished" | "short" | "letters" | "unknown";

export interface WordleDailyEntry {
  username: string;
  currentStreak: number;
  maxStreak: number;
}

export interface WordleUnlimitedEntry {
  username: string;
  bestGuesses: number;
  currentStreak: number;
  maxStreak: number;
}

export interface WordleLeaderboard {
  dailyStreakEntries: WordleDailyEntry[];
  unlimitedBestEntries: WordleUnlimitedEntry[];
  resetVersion: number;
}

export interface WordlePublicRound {
  id: string;
  mode: WordleMode;
  dayKey: string;
  guesses: string[];
  evaluations: WordleColor[][];
  finished: boolean;
  won: boolean;
  maxGuesses: number;
  wordLength: number;
  /** Present only after the round is finished. */
  answer?: string;
}

export interface WordleLocalStats {
  played: number;
  won: number;
  currentStreak: number;
  maxStreak: number;
  guessDistribution: [number, number, number, number, number, number];
}

export interface WordleApplyOk {
  ok: true;
  guesses: string[];
  evaluation: WordleColor[];
  finished: boolean;
  won: boolean;
}

export interface WordleApplyFail {
  ok: false;
  reason: WordleGuessReject;
}

const COLOR_RANK: Record<WordleColor, number> = {
  absent: 1,
  present: 2,
  correct: 3,
};

export function emptyWordleStats(): WordleLocalStats {
  return {
    played: 0,
    won: 0,
    currentStreak: 0,
    maxStreak: 0,
    guessDistribution: [0, 0, 0, 0, 0, 0],
  };
}

export function normalizeWordleGuess(raw: string): string {
  return raw.trim().toLocaleUpperCase("pl-PL").normalize("NFC");
}

export function isWordleLetter(key: string): boolean {
  return WORDLE_LETTER_RE.test(key);
}

export function isWordleWordShape(word: string): boolean {
  return WORDLE_WORD_RE.test(word);
}

/**
 * Standard Wordle colouring: greens first, then yellows from leftover counts.
 * `Ł` is not `L`. Duplicate letters cannot go yellow once their budget is spent.
 */
export function scoreWordleGuess(guess: string, answer: string): WordleColor[] {
  const result: WordleColor[] = Array.from({ length: WORDLE_WORD_LENGTH }, () => "absent");
  const remaining: Record<string, number> = {};

  for (let i = 0; i < WORDLE_WORD_LENGTH; i += 1) {
    const guessCh = guess[i];
    const answerCh = answer[i];
    if (guessCh === undefined || answerCh === undefined) continue;
    if (guessCh === answerCh) {
      result[i] = "correct";
    } else {
      remaining[answerCh] = (remaining[answerCh] ?? 0) + 1;
    }
  }

  for (let i = 0; i < WORDLE_WORD_LENGTH; i += 1) {
    if (result[i] === "correct") continue;
    const ch = guess[i];
    if (ch === undefined) continue;
    const left = remaining[ch] ?? 0;
    if (left > 0) {
      result[i] = "present";
      remaining[ch] = left - 1;
    }
  }

  return result;
}

export function wordleEvaluations(
  guesses: readonly string[],
  answer: string,
): WordleColor[][] {
  return guesses.map((guess) => scoreWordleGuess(guess, answer));
}

export function wordleOutcome(
  guesses: readonly string[],
  answer: string,
): { finished: boolean; won: boolean } {
  const won = guesses.includes(answer);
  return {
    won,
    finished: won || guesses.length >= WORDLE_MAX_GUESSES,
  };
}

export function applyWordleGuess(
  guesses: readonly string[],
  answer: string,
  rawGuess: string,
  allowed: ReadonlySet<string>,
): WordleApplyOk | WordleApplyFail {
  const { finished } = wordleOutcome(guesses, answer);
  if (finished) return { ok: false, reason: "finished" };

  const guess = normalizeWordleGuess(rawGuess);
  if (guess.length < WORDLE_WORD_LENGTH) return { ok: false, reason: "short" };
  if (!isWordleWordShape(guess)) return { ok: false, reason: "letters" };
  if (!allowed.has(guess)) return { ok: false, reason: "unknown" };

  const next = [...guesses, guess];
  const outcome = wordleOutcome(next, answer);
  return {
    ok: true,
    guesses: next,
    evaluation: scoreWordleGuess(guess, answer),
    finished: outcome.finished,
    won: outcome.won,
  };
}

export function mergeWordleKeyColor(
  current: WordleColor | undefined,
  incoming: WordleColor,
): WordleColor {
  if (!current) return incoming;
  return COLOR_RANK[incoming] > COLOR_RANK[current] ? incoming : current;
}

export function wordleKeyboardColors(
  guesses: readonly string[],
  evaluations: readonly WordleColor[][],
): Record<string, WordleColor> {
  const keyState: Record<string, WordleColor> = {};
  for (let idx = 0; idx < guesses.length; idx += 1) {
    const guess = guesses[idx];
    const colors = evaluations[idx];
    if (!guess || !colors) continue;
    for (let i = 0; i < WORDLE_WORD_LENGTH; i += 1) {
      const letter = guess[i];
      const color = colors[i];
      if (!letter || !color) continue;
      keyState[letter] = mergeWordleKeyColor(keyState[letter], color);
    }
  }
  return keyState;
}

export function getWarsawDayKey(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: WORDLE_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  if (!year || !month || !day) {
    throw new Error("Could not format Warsaw day key");
  }
  return `${year}-${month}-${day}`;
}

export function isWordleDayKey(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** Calendar yesterday of a `YYYY-MM-DD` Warsaw day key. */
export function wordleYesterdayKey(dayKey: string): string {
  const [yearRaw, monthRaw, dayRaw] = dayKey.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const utc = Date.UTC(year, month - 1, day);
  const previous = new Date(utc - 86_400_000);
  const y = previous.getUTCFullYear();
  const m = String(previous.getUTCMonth() + 1).padStart(2, "0");
  const d = String(previous.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function dailyIndexFromKey(dayKey: string): number {
  const [yearRaw, monthRaw, dayRaw] = dayKey.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/** FNV-1a, same mixing as the legacy client daily picker. */
export function hashDayKeyToUInt(dayKey: string): number {
  let hash = 2166136261;
  for (let i = 0; i < dayKey.length; i += 1) {
    hash ^= dayKey.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function selectDailyAnswer(dayKey: string, answers: readonly string[]): string {
  if (answers.length === 0) {
    throw new Error("Empty Wordle answer list");
  }
  const mixedSeed = `${dayKey}:${dailyIndexFromKey(dayKey)}`;
  const index = hashDayKeyToUInt(mixedSeed) % answers.length;
  return answers[index] ?? answers[0] ?? "";
}

export function pickWordleAnswer(
  answers: readonly string[],
  exceptWord = "",
  rng: () => number = Math.random,
): string {
  if (answers.length === 0) {
    throw new Error("Empty Wordle answer list");
  }
  const first = answers[0];
  if (first === undefined) {
    throw new Error("Empty Wordle answer list");
  }
  if (answers.length === 1) return first;
  for (let attempt = 0; attempt < 24; attempt += 1) {
    const index = Math.floor(rng() * answers.length);
    const picked = answers[index];
    if (picked !== undefined && picked !== exceptWord) return picked;
  }
  return first;
}

export function recordWordleFinish(
  stats: WordleLocalStats,
  won: boolean,
  guessesCount: number,
): WordleLocalStats {
  const guessDistribution: WordleLocalStats["guessDistribution"] = [
    ...stats.guessDistribution,
  ];
  if (won && guessesCount >= 1 && guessesCount <= WORDLE_MAX_GUESSES) {
    const slot = guessesCount - 1;
    const current = guessDistribution[slot] ?? 0;
    guessDistribution[slot] = current + 1;
  }

  const currentStreak = won ? stats.currentStreak + 1 : 0;
  return {
    played: stats.played + 1,
    won: won ? stats.won + 1 : stats.won,
    currentStreak,
    maxStreak: won ? Math.max(stats.maxStreak, currentStreak) : stats.maxStreak,
    guessDistribution,
  };
}

export function wordleRejectMessage(reason: WordleGuessReject): string {
  if (reason === "finished") return "Gra zakończona";
  if (reason === "short") return "Za mało liter";
  if (reason === "letters") return "Wymagane są poprawne polskie znaki";
  return "Nieznane słowo";
}

export function toPublicWordleRound(input: {
  id: string;
  mode: WordleMode;
  dayKey: string;
  guesses: readonly string[];
  answer: string;
}): WordlePublicRound {
  const guesses = [...input.guesses];
  const evaluations = wordleEvaluations(guesses, input.answer);
  const { finished, won } = wordleOutcome(guesses, input.answer);
  const round: WordlePublicRound = {
    id: input.id,
    mode: input.mode,
    dayKey: input.dayKey,
    guesses,
    evaluations,
    finished,
    won,
    maxGuesses: WORDLE_MAX_GUESSES,
    wordLength: WORDLE_WORD_LENGTH,
  };
  if (finished) {
    round.answer = input.answer;
  }
  return round;
}
